import React, { useState } from 'react';
import Button from './Button';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (message: string) => void;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  showMessageInput?: boolean;
  messagePlaceholder?: string;
  requiredConfirmationText?: string;
  confirmationFieldLabel?: string;
  confirmationFieldPlaceholder?: string;
  confirmButtonVariant?: 'primary' | 'outline' | 'secondary';
  isLoading?: boolean;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Bekræft',
  cancelLabel = 'Annuller',
  showMessageInput = false,
  messagePlaceholder = 'Skriv en besked...',
  requiredConfirmationText,
  confirmationFieldLabel,
  confirmationFieldPlaceholder,
  confirmButtonVariant = 'primary',
  isLoading = false,
}) => {
  const [message, setMessage] = useState('');
  const [confirmationText, setConfirmationText] = useState('');

  if (!isOpen) return null;

  const isConfirmationValid = requiredConfirmationText
    ? confirmationText.trim() === requiredConfirmationText
    : true;

  const handleConfirm = () => {
    if (!isConfirmationValid) return;
    onConfirm(message);
    setMessage('');
    setConfirmationText('');
  };

  const handleClose = () => {
    setMessage('');
    setConfirmationText('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={handleClose}
        />
        
        {/* Dialog */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
          <p className="text-sm text-gray-600 mb-4 whitespace-pre-line">{description}</p>
          
          {showMessageInput && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Besked til lejer (valgfri)
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={messagePlaceholder}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
              />
            </div>
          )}

          {requiredConfirmationText && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {confirmationFieldLabel || `Skriv "${requiredConfirmationText}" for at bekræfte`}
              </label>
              <input
                type="text"
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                placeholder={confirmationFieldPlaceholder || requiredConfirmationText}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          )}
          
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={handleClose} disabled={isLoading}>
              {cancelLabel}
            </Button>
            <Button variant={confirmButtonVariant} onClick={handleConfirm} isLoading={isLoading} disabled={isLoading || !isConfirmationValid}>
              {confirmLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
